<template>
  <div id="app" class="app-shell">
    <header class="app-header">
      <div class="app-logo">
        <RouterLink to="/" class="logo-link" @click="closeDrawer">
          <span class="logo-text">Honest Pitches</span>
          <span class="logo-mark" aria-hidden="true">HP</span>
        </RouterLink>
      </div>

      <button
        class="nav-toggle"
        type="button"
        :aria-expanded="drawerOpen"
        aria-controls="primary-nav"
        :aria-label="drawerOpen ? 'Close navigation' : 'Open navigation'"
        @click="toggleDrawer"
        data-testid="nav-toggle"
      >
        <span class="nav-toggle-bar" :class="{ open: drawerOpen }"></span>
        <span class="nav-toggle-bar nav-toggle-bar--middle" :class="{ open: drawerOpen }"></span>
        <span class="nav-toggle-bar" :class="{ open: drawerOpen }"></span>
      </button>

      <nav
        id="primary-nav"
        class="app-nav"
        :data-open="drawerOpen ? 'true' : 'false'"
        aria-label="Main navigation"
      >
        <!-- Creator-scoped links (visible on every viewport). -->
        <RouterLink to="/" class="nav-link" :class="{ active: route.name === 'dashboard' }" @click="closeDrawer">
          My Pitches
        </RouterLink>
        <RouterLink
          to="/media"
          class="nav-link"
          :class="{ active: route.name === 'media-library' }"
          @click="closeDrawer"
          data-testid="nav-media-library"
        >
          My Media
        </RouterLink>
        <!-- 2026-07-09 (Pitch V3 cutover): sidebar "New Pitch" goes
             to the picker (/pitches/new/choose) so creators get
             both V3 + V2. The picker defaults to V3 but offers V2
             for legacy users. See
             docs-new/pitch-v3-iterative-flow.md §"Route swap". -->
        <RouterLink to="/pitches/new/choose" class="nav-link" @click="closeDrawer">
          <span aria-hidden="true">+</span> New Pitch
        </RouterLink>
        <RouterLink
          to="/activity"
          class="nav-link"
          :class="{ active: route.name === 'activity' }"
          @click="closeDrawer"
        >
          Activity
        </RouterLink>

        <!--
          User-scoped items (mobile-only, gated on auth). The
          profile menu owns these on desktop. Duplicated here
          so a phone user doesn't have to tap a tiny avatar
          to reach Account / Settings / Billing / Sign out.
          Cross-reference: src/components/ProfileMenu.vue —
          any new link added there must also be added here.
        -->
        <template v-if="auth.isLoggedIn">
          <!--
            Identity block (avatar + name + email). Shares
            useUserIdentity with the desktop profile menu so
            both surfaces show the same identity. The block
            is the FIRST thing in the drawer's logged-in
            surface — a phone user has no other way to tell
            who they're logged in as once the desktop profile
            menu is hidden. The block is wrapped in
            `nav-identity` so CSS can hide it on desktop and
            give it its own padding/background in the drawer.
          -->
          <div
            class="drawer-identity"
            data-testid="drawer-identity"
            role="group"
            aria-label="Signed in as"
          >
            <img
              v-if="identity.avatarImgSrc.value"
              :src="identity.avatarImgSrc.value"
              :alt="`Avatar for ${identity.displayLabel.value}`"
              class="drawer-identity-avatar-img"
              width="40"
              height="40"
            />
            <span
              v-else
              class="drawer-identity-avatar-letter"
              aria-hidden="true"
            >
              {{ identity.avatarLetter.value }}
            </span>
            <div class="drawer-identity-text">
              <div class="drawer-identity-name">
                {{ identity.displayLabel.value }}
              </div>
              <div
                v-if="identity.userEmail.value"
                class="drawer-identity-email"
              >
                {{ identity.userEmail.value }}
              </div>
            </div>
          </div>

          <hr class="nav-sep nav-sep--mobile" />

          <RouterLink
            to="/account"
            class="nav-link nav-link--mobile"
            data-testid="drawer-account"
            @click="closeDrawer"
          >
            My account
          </RouterLink>
          <RouterLink
            to="/settings"
            class="nav-link nav-link--mobile"
            @click="closeDrawer"
          >
            Settings
          </RouterLink>
          <RouterLink
            to="/settings/api-keys"
            class="nav-link nav-link--mobile"
            @click="closeDrawer"
          >
            API Keys
          </RouterLink>
          <RouterLink
            to="/billing"
            class="nav-link nav-link--mobile"
            @click="closeDrawer"
          >
            Billing
          </RouterLink>

          <hr class="nav-sep nav-sep--mobile" />

          <button
            type="button"
            class="nav-link nav-link--mobile nav-link--signout"
            data-testid="drawer-signout"
            :disabled="auth.loading"
            @click="onDrawerSignOut"
          >
            {{ auth.loading ? "Signing out…" : "Sign out" }}
          </button>
        </template>
      </nav>

      <!--
        2026-06-25 restructure: the profile menu lives in the
        header ONLY on desktop. On mobile (≤768 px) it is
        hidden via CSS and the same items render INSIDE the
        .app-nav drawer so the operator can reach them without
        a tiny avatar click. The shell is the LAST child of
        the header in DOM order so `margin-left: auto` floats
        it to the far-right edge regardless of inline-nav
        width. See App.test.ts for the layout contract.
      -->
      <div class="profile-menu-shell" data-testid="profile-menu-shell">
        <ProfileMenu v-if="auth.isLoggedIn" />
      </div>
    </header>

    <main class="app-main">
      <RouterView />
    </main>
    <!--
      Single instance of the destructive-action modal that
      ALL delete/archive flows call into (PitchLifecyclePanel,
      PitchEdit, Dashboard archived-tab Delete-permanently). Pinia-
      backed so each view calls `await useConfirmStore().request()`
      and gets a Promise<boolean>; the store queues concurrent
      requests and the modal renders once, no matter who asked.
    -->
    <AppConfirmModal />
    <!--
      2026-07-18: global toast viewport. Mounted once near the
      app root so every Vue component can call
      `useToast().push(...)` and the resulting notification
      renders in one shared, tone-aware stack. Replaces the
      page-local `flashToast` + `<p class="save-toast">` pair
      that lived in `views/PitchAudienceQuestions.vue` (the
      only toast that existed before 2026-07-18; that pair
      miscolored errors as green and auto-dismissed after 3s,
      which silently hid the failure). Per the operator's
      2026-07-18 directive: errors are sticky + role="alert",
      success is auto-dismiss after 3s, info/warning after 5s.
      See @honest-pitches/toast/README.md for the full contract.
    -->
    <ToastViewport />
  </div>
</template>

<script setup lang="ts">
import "@honest-pitches/toast/vue/style.css";
import { onBeforeUnmount, ref, watch } from "vue";
import { RouterLink, RouterView, useRoute, useRouter } from "vue-router";
import { useAuthStore } from "./lib/auth";
import { useUserIdentity } from "./lib/user-identity";
import ProfileMenu from "./components/ProfileMenu.vue";
import AppConfirmModal from "./components/AppConfirmModal.vue";
import ToastViewport from "@honest-pitches/toast/vue/ToastViewport.vue";

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

// The mobile drawer's identity block (avatar + name + email)
// shares the same composable as the desktop profile-menu
// dropdown, so the two surfaces can never drift on "who am
// I?". See src/lib/user-identity.ts for the avatar chain and
// display-label rules.
const identity = useUserIdentity();

const drawerOpen = ref(false);

function toggleDrawer() {
  drawerOpen.value = !drawerOpen.value;
}

function closeDrawer() {
  drawerOpen.value = false;
}

async function onDrawerSignOut() {
  // Mirror ProfileMenu#onSignOut: log out, then route to
  // /auth so the auth guard lets the user past. The router
  // push closes the drawer via the route-change watcher.
  closeDrawer();
  await auth.logout();
  try {
    await router.push({ name: "auth" });
  } catch {
    /* no-op: ignore navigation race */
  }
}

function syncBodyLock(open: boolean) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("nav-open", open);
}

function syncViewportForRoute() {
  // On any route change, collapse the drawer so the new view paints cleanly.
  if (drawerOpen.value) closeDrawer();
}

watch(drawerOpen, (open) => syncBodyLock(open));

// Close drawer on Escape — keyboard accessibility for the hamburger.
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && drawerOpen.value) closeDrawer();
}

if (typeof window !== "undefined") {
  window.addEventListener("keydown", onKeydown);
}

onBeforeUnmount(() => {
  syncBodyLock(false);
  if (typeof window !== "undefined") {
    window.removeEventListener("keydown", onKeydown);
  }
});

// Auto-close the drawer on route change so the user doesn't navigate "into a hidden panel".
watch(() => route.fullPath, syncViewportForRoute);
</script>

<style>
/* App-shell layout */
.app-shell {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

/* Header bar */
.app-header {
  background: var(--hp-surface);
  border-bottom: 1px solid var(--hp-border);
  padding: 0 1rem;
  min-height: 56px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: sticky;
  top: 0;
  z-index: 100;
  padding-top: var(--safe-top);
}

.app-logo .logo-link {
  font-weight: 800;
  font-size: 1rem;
  color: var(--hp-text);
  text-decoration: none;
  letter-spacing: -0.02em;
  display: inline-flex;
  align-items: center;
  min-height: var(--tap-target);
}

.logo-mark { display: none; }

.app-nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/*
 * Profile-menu shell group (DESKTOP ONLY — hidden on mobile).
 * DOM order is: logo, nav-toggle, app-nav, profile-menu-shell.
 * The shell carries `margin-left: auto` so it floats to the
 * far-right edge of the header regardless of inline-nav width.
 * The mobile media query below hides this shell entirely so the
 * profile-menu's items render inside the drawer (.app-nav)
 * instead. See App.test.ts for the layout contract.
 */
.profile-menu-shell {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.nav-link {
  /* Apple HIG / Material touch target — see --tap-target in main.css. */
  min-height: var(--tap-target);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-sm);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--hp-text-muted);
  transition: background 0.1s, color 0.1s;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
}

.nav-link:hover,
.nav-link.active {
  background: var(--hp-bg);
  color: var(--hp-text);
}

.nav-logout {
  color: var(--hp-danger);
}
.nav-logout:hover {
  background: var(--hp-danger-surface, #fef2f2);
  color: var(--hp-danger);
}

.app-main {
  flex: 1;
}

/* Mobile-only drawer items (user-scoped). Hidden on desktop
   by default; the mobile media query below flips them on. */
.nav-link--mobile,
.nav-sep--mobile {
  display: none;
}

.nav-sep--mobile {
  width: 100%;
  border: 0;
  border-top: 1px solid var(--hp-border);
  margin: 0;
}

/*
 * Identity block — drawn at the top of the drawer on mobile.
 * Hidden on desktop (it's a duplicate of the desktop profile
 * menu's own identity surface, which would compete for visual
 * attention). On mobile the drawer is the only place the
 * operator can see "who am I?"; the block is sized for the
 * tap-target guideline (44 px avatar row) so the touch area
 * is comfortable.
 */
.drawer-identity {
  display: none;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--hp-border);
  background: var(--hp-bg);
  min-height: var(--tap-target);
}

.drawer-identity-avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}

.drawer-identity-avatar-letter {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--hp-surface, #fff);
  border: 1px solid var(--hp-border);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: var(--hp-text-muted, #6b7280);
  flex: 0 0 auto;
}

.drawer-identity-text {
  flex: 1 1 auto;
  min-width: 0; /* allow word-break inside the flex child */
}

.drawer-identity-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--hp-text, #111827);
  line-height: 1.25;
  word-break: break-word;
}

.drawer-identity-email {
  font-size: 0.8125rem;
  color: var(--hp-text-muted, #6b7280);
  line-height: 1.25;
  word-break: break-word;
  margin-top: 0.125rem;
}

.nav-link--signout {
  color: var(--hp-danger);
  text-align: left;
}
.nav-link--signout:hover {
  background: var(--hp-danger-surface, #fef2f2);
  color: var(--hp-danger);
}
.nav-link--signout:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Hamburger toggle — hidden on desktop, shown at ≤768px. */
.nav-toggle {
  display: none;
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  width: var(--tap-target);
  height: var(--tap-target);
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: var(--radius-sm);
}

.nav-toggle:hover { background: var(--hp-bg); }

.nav-toggle-bar {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--hp-text);
  border-radius: 2px;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.nav-toggle-bar--middle.open { opacity: 0; }
.nav-toggle-bar.open:first-child { transform: translateY(6px) rotate(45deg); }
.nav-toggle-bar.open:last-child  { transform: translateY(-6px) rotate(-45deg); }

/* ─── Mobile (≤768px) ────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .app-header { padding: 0 0.5rem; }

  /*
   * Hamburger is the only header item on mobile (logo at
   * left, hamburger at far right). The profile menu shell
   * is hidden below; its items render inside the drawer
   * via .nav-link--mobile + .nav-sep--mobile.
   */
  .nav-toggle {
    display: flex;
    margin-left: auto;
  }

  /* Hide the profile-menu shell on mobile — its items
     now live inside the drawer. */
  .profile-menu-shell {
    display: none;
  }

  .app-nav {
    /* Slide-down drawer below the sticky header. */
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    background: var(--hp-surface);
    border-bottom: 1px solid var(--hp-border);
    box-shadow: var(--shadow-md);
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.2s ease;
    padding-top: var(--safe-top);
  }

  .app-nav[data-open="true"] {
    max-height: calc(100dvh - 56px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .app-nav .nav-link {
    width: 100%;
    border-radius: 0;
    padding: 0.875rem 1rem;
    border-bottom: 1px solid var(--hp-border);
    justify-content: flex-start;
  }

  .app-nav .nav-link:last-child { border-bottom: 0; }

  /* Flip on the mobile-only user-scoped items. */
  .nav-link--mobile {
    display: flex;
  }
  .nav-sep--mobile {
    display: block;
  }

  /* Identity block — only visible inside the mobile drawer.
     On desktop, the profile menu's own panel renders the same
     surface, so we hide this one to avoid two competing
     identity surfaces at once. */
  .drawer-identity {
    display: flex;
  }
}

/* ─── Small mobile (≤480px) — collapse logo to mark ──────────────────── */
@media (max-width: 480px) {
  .logo-text { display: none; }
  .logo-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: var(--radius-sm);
    background: var(--hp-primary);
    color: var(--hp-surface);
    font-weight: 800;
    font-size: 0.875rem;
  }
}
</style>
